<template>
  <base-modal
    :id="'purchase-modal'"
    :title="'Confirm your purchase'"
    @closeModal="onCloseModal"
  >
    <div class="purchase-modal-wrapper">
      <div class="content">
        <div class="row-wrapper">
          <div class="label">
            Item Name
          </div>
          <div>{{ name }}</div>
        </div>
        <div class="row-wrapper">
          <div class="label">
            Selling Method
          </div>
          <div>{{ getSellMethodByAddress() }}</div>
        </div>
        <div class="row-wrapper">
          <div class="label">
            Seller
          </div>
          <div class="address">
            {{ sellerAddress | blockchainAddress }}
          </div>
        </div>
        <div class="row-wrapper">
          <div class="label">
            Price
          </div>
          <div>
            {{ (price || 0) | formatWeiAmount }}
            {{ currency | transformToCurrencyName }}
          </div>
        </div>
      </div>
      <div class="footer">
        <primary-button class="confirm-btn" @onClick="buyNFT">
          <template v-if="isPurchasing">
            <b-icon icon="arrow-clockwise" animation="spin" font-scale="2" />
          </template>
          <template v-else>
            Confirm
          </template>
        </primary-button>
        <filter-option-btn
          class="cancel-btn"
          :is-disable="isPurchasing"
          @onClick="onCloseModal"
        >
          Cancel
        </filter-option-btn>
      </div>
    </div>
  </base-modal>
</template>
<script>
import PrimaryButton from '@/components/BaseComponents/PrimaryButton.vue';
import FilterOptionBtn from '@/components/BaseComponents/FilterOptionBtn.vue';
import BaseModal from '@/components/BaseModal.vue';
import {
  getNftTax,
  sleep,
  convertDecToWei,
  handleTransactionError,
  getSellMethod
} from '@/helpers/index';
import {
  getMarketPriceOfNft,
  purchaseMarketListing,
  getDutchAuctionFinalPrice,
  purchaseDutchAuctionListing
} from '@/helpers/utils';
import BigNumber from 'bignumber.js';
import { mapGetters } from 'vuex';
import { MARKET_ADDRESSES } from '@/constans';
import config from '@/config';

export default {
  components: {
    PrimaryButton,
    FilterOptionBtn,
    BaseModal
  },
  props: {
    marketContract: {
      type: String,
      default: ''
    },
    nft: {
      type: Object,
      default: () => ({})
    },
    sellerAddress: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      amount: '',
      price: 0,
      currency: '',
      isPurchasing: false
    };
  },
  computed: {
    ...mapGetters('myAccount', ['myBalances']),
    name() {
      return this.nft.name || '';
    },
    nftContract() {
      return this.nft.contract;
    },
    nftId() {
      return this.nft.id;
    },
    isDutchAuctionMarket() {
      return this.marketContract === MARKET_ADDRESSES.dutchAuctionMarketplace;
    },
    isArtWork() {
      return this.nftContract === config.addresses.artworkNftAddress;
    }
  },
  methods: {
    getSellMethod,
    convertDecToWei,
    handleTransactionError,
    getNftTax,
    onCloseModal() {
      if (this.isPurchasing) return;
      this.$bvModal.hide('purchase-modal');
    },
    async buyNFT() {
      const userBalance = this.myBalances[this.currency];
      let price, currency;
      if (this.isDutchAuctionMarket) {
        [price, currency] = await getDutchAuctionFinalPrice(
          this.nftContract,
          this.nftId
        );
        price = price.toString();
      } else {
        [price, currency] = await getMarketPriceOfNft(
          this.nftContract,
          this.nftId
        );
      }
      if (new BigNumber(price).comparedTo(this.price) !== 0) {
        this.$dialog.notify.warning(
          'The price of the listing has changed. Please refresh listing and try again'
        );
        return;
      }
      if (currency.toLowerCase() !== this.currency.toLowerCase()) {
        this.$dialog.notify.warning(
          'The currency of the listing has changed. Please refresh listing and try again'
        );
        return;
      }
      if (new BigNumber(userBalance).comparedTo(price) < 0) {
        this.$dialog.notify.error('Your balance is not enough!');
        return;
      }
      this.isPurchasing = true;

      try {
        let tx = {};
        if (this.isDutchAuctionMarket) {
          tx = await purchaseDutchAuctionListing(
            this.nftContract,
            this.nftId,
            this.convertDecToWei(this.price),
            this.currency
          );
        } else {
          tx = await purchaseMarketListing(
            this.nftContract,
            this.nftId,
            this.convertDecToWei(this.price),
            this.currency,
            this.isArtWork
          );
        }
        if (tx.transactionHash) {
          await sleep(9000);
          this.$dialog.notify.success("You've bought NFT successfully!");
          this.$router.push({ name: 'my-collection' });
        } else {
          this.$dialog.notify.error('An error has occurred. Please try again!');
        }
      } catch (error) {
        this.isPurchasing = false;
        const message = this.handleTransactionError(error);
        this.$dialog.notify.error(message);
      }
      this.isPurchasing = false;
      this.$bvModal.hide('purchase-modal');
    },
    async fetchDutchAuctionFinalPrice() {
      if (!this.nftContract || !this.nftId) return;
      const [price, currency] = await getDutchAuctionFinalPrice(
        this.nftContract,
        this.nftId
      );
      this.price = price.toString();
      this.currency = currency;
    },
    async fetchOnChainCurrentPrice() {
      if (!this.nftContract || !this.nftId) return;
      const [price, currency] = await getMarketPriceOfNft(
        this.nftContract,
        this.nftId
      );
      this.price = price;
      this.currency = currency;
    },
    init() {
      if (this.isDutchAuctionMarket) {
        this.fetchDutchAuctionFinalPrice();
      } else {
        this.fetchOnChainCurrentPrice();
      }
    },
    getSellMethodByAddress() {
      if (this.isDutchAuctionMarket) {
        return 'Dutch Auction';
      }
      return 'Fixed Price';
    }
  }
};
</script>
<style lang="scss">
.error {
  color: red;
  margin-top: 20px;
  margin-left: 20px;
}
.content {
  padding: 0 20px;
  font-family: Inter-Medium;
  .row-wrapper {
    display: flex;
    justify-content: space-between;
    margin: 7px 0;
    height: 35px;
    .label {
      color: #798392;
      font-size: 16px;
      font-family: Inter-Regular;
      line-height: 35px;
    }
    .final-price {
      line-height: 35px;
    }
    .address {
      color: #00a3fe;
      line-height: 35px;
    }
    input {
      color: #798392;
      border: 1px solid #e3e6e9;
      border-radius: 6px;
      padding: 4px 10px;
      margin-right: 4px;
      width: 100px;
      &.expiration {
        width: 200px;
      }
    }
  }
}
.footer {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 30px 0;

  .cancel-btn {
    width: 48%;
    margin-left: 5px;
    height: 48px;
  }
  .confirm-btn {
    width: 48%;
    height: 48px;
    margin-right: 5px;
  }
}
</style>
